import React from 'react';
import { List } from 'immutable';
import CardFace from '../Card/CardFace';
import { DeckStack } from '../Deck/styled';
import { PlainButton } from '../Controls';

/**
 * What's in one of a player's spots as a picture, from a board picture the server sends (BoardPicture): the same
 * cards the game shows there, face down where the viewer can't see them. For the games list's small boards and the
 * replay; given `onPick`, a face-up card or the discard pile can be tapped (the replay shows it bigger).
 *
 * @param {Map|undefined} side one player's side of the picture
 * @param {Map} cardsById the card catalog
 * @param {function(string, ?Map, Map): void} [onPick] called with the zone, the card tapped (null for a pile) and the side
 * @returns {function(string, number): ?object} the spot's contents, for MatLayout
 */
export const pictureSpot = (side, cardsById, onPick = null) => (zone, position) => {
  if (!side) {
    return null;
  }
  const content = spotContents(side, cardsById, zone, position);
  const card = zone === 'active' || zone === 'bench' ? pokemonCard(side, cardsById, zone, position) : null;
  const tappable = onPick && content && (card || (zone === 'discard' && side.get('discard')));
  return tappable
    ? <PlainButton onClick={() => onPick(zone, card, side)} aria-label={card ? card.get('name') : 'Discard pile'}>{content}</PlainButton>
    : content;
};

/** The Pokémon in a spot, if it's face up. */
function pokemonCard(side, cardsById, zone, position) {
  const spot = zone === 'active' ? side.get('active') : side.get('bench', List()).find(s => s.get('position') === position);
  return spot && spot.get('card_id') ? cardsById.get(spot.get('card_id')) : null;
}

/** What a spot shows, as a picture. */
function spotContents(side, cardsById, zone, position) {
  const pokemon = (spot) => spot && (
    <CardFace
      card={spot.get('card_id') ? cardsById.get(spot.get('card_id')) : undefined}
      damage={spot.get('damage')}
      conditions={spot.get('conditions')}
      energyAttachments={spot.get('energy')}
      trainers={(spot.get('trainers') || List()).map(id => cardsById.get(id)).filter(Boolean)}
    />
  );
  switch (zone) {
    case 'prize':
      return side.get('prizes', List()).includes(position) ? <CardFace label="Prize card" /> : null;
    case 'active':
      return pokemon(side.get('active'));
    case 'bench':
      return pokemon(side.get('bench', List()).find(spot => spot.get('position') === position));
    case 'deck':
      return side.get('deck') ? <DeckStack $count={side.get('deck')}><CardFace count={side.get('deck')} /></DeckStack> : null;
    case 'discard':
      return side.get('discard_top') ? <CardFace card={cardsById.get(side.get('discard_top'))} count={side.get('discard')} /> : null;
    default:
      return null;
  }
}
